import { PlayIcon } from '@radix-ui/react-icons';
import { Flex, IconButton, Switch } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { FlexTableWrapper } from 'components/common/layout/flex-table-wrapper';
import { CommonTableHoC } from 'components/common/table';
import { HitterSessionHeader } from 'components/sections/analytics/hitter-session/header';
import { HitterSessionToolbar } from 'components/sections/analytics/hitter-session/toolbar';
import { HitterSessionVideoDialog } from 'components/sections/analytics/hitter-session/video-dialog';
import useAnalyticsStore from 'components/sections/analytics/store/use-analytics-store';
import { MachineContext } from 'contexts/machine.context';
import { SectionsContext } from 'contexts/sections.context';
import { ACTIONS_KEY, TABLES } from 'enums/tables';
import { t } from 'i18next';
import { TableIdentifier } from 'interfaces/cookies/i-app.cookie';
import { ITableColumn } from 'interfaces/tables/columns';
import { ITablePageable } from 'interfaces/tables/pagination';
import { ITableSortable } from 'interfaces/tables/sorting';
import { round } from 'lib_ts/classes/math.utilities';
import { tooltipPitchType } from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IExpiringUrlDict } from 'lib_ts/interfaces/common/i-url-dict';
import {
  IFireEventDetails,
  ISessionHit,
} from 'lib_ts/interfaces/i-session-hit';
import { useContext, useEffect, useMemo, useState } from 'react';
import { MainService } from 'services/main.service';

const IDENTIFIER = TableIdentifier.HitterSession;
const PAGE_SIZES = TABLES.PAGE_SIZES.MD;

const VALUE_FALLBACK = '-';

const roundToZero = (v?: number) => (v?.toFixed(0) ? round(v, 0) : null);
const roundToOne = (v?: number) => (v?.toFixed(1) ? round(v, 1) : null);

interface IGenerateHitterSessionColumnsParams {
  onUpdate: (id: string, value: Partial<ISessionHit>) => void;
  onPlay: (fire: IFireEventDetails) => void;
}

const generateHitterSessionColumns = ({
  onUpdate,
  onPlay,
}: IGenerateHitterSessionColumnsParams): ITableColumn[] => [
  {
    label: 'common.pitch',
    key: 'pitch',
    formatFn: ({ pitch }: ISessionHit) => pitch?.name ?? '',
    sortRowsFn: (a: ISessionHit, b: ISessionHit, dir: number) =>
      (b.pitch?.name ?? '').localeCompare(a.pitch?.name ?? '') * dir,
  },
  {
    label: 'common.type',
    key: 'type',
    formatFn: ({ pitch }: ISessionHit) => pitch?.type,
    tooltipFn: ({ pitch }: ISessionHit) =>
      t(tooltipPitchType(pitch?.type)).toString(),
    sortRowsFn: (a: ISessionHit, b: ISessionHit, dir: number) =>
      (b.pitch?.type ?? '').localeCompare(a.pitch?.type ?? '') * dir,
  },
  {
    label: 'common.speed',
    subLabel: 'mph',
    key: 'speed',
    align: 'right',
    formatFn: ({ pitch }: ISessionHit) =>
      roundToOne(pitch?.speedMPH) ?? VALUE_FALLBACK,
    sortRowsFn: (a: ISessionHit, b: ISessionHit, dir: number) =>
      ((a.pitch?.speedMPH ?? 0) - (b.pitch?.speedMPH ?? 0)) * dir,
  },
  {
    label: 'common.v-break',
    subLabel: 'in',
    key: 'zBreakIN',
    align: 'right',
    formatFn: ({ pitch }: ISessionHit) =>
      roundToOne(pitch?.zBreakIN) ?? VALUE_FALLBACK,
    sortRowsFn: (a: ISessionHit, b: ISessionHit, dir: number) =>
      ((a.pitch?.zBreakIN ?? 0) - (b.pitch?.zBreakIN ?? 0)) * dir,
  },
  {
    label: 'common.h-break',
    subLabel: 'in',
    key: 'xBreakIN',
    align: 'right',
    formatFn: ({ pitch }: ISessionHit) =>
      roundToOne(pitch?.xBreakIN) ?? VALUE_FALLBACK,
    sortRowsFn: (a: ISessionHit, b: ISessionHit, dir: number) =>
      ((a.pitch?.xBreakIN ?? 0) - (b.pitch?.xBreakIN ?? 0)) * dir,
  },
  {
    label: 'common.result',
    key: 'result',
    formatFn: ({ hit }: ISessionHit) =>
      hit?.invalid ? VALUE_FALLBACK : hit?.outcome || VALUE_FALLBACK,
    sortRowsFn: (a: ISessionHit, b: ISessionHit, dir: number) =>
      (a.hit?.outcome ?? '').localeCompare(b.hit?.outcome ?? '') * dir,
  },
  {
    label: 'common.ev',
    subLabel: 'mph',
    key: 'exitMPH',
    align: 'right',
    formatFn: ({ hit }: ISessionHit) =>
      hit?.invalid
        ? VALUE_FALLBACK
        : (roundToOne(hit?.exitMPH) ?? VALUE_FALLBACK),
    sortRowsFn: (a: ISessionHit, b: ISessionHit, dir: number) =>
      ((a.hit?.exitMPH ?? 0) - (b.hit?.exitMPH ?? 0)) * dir,
  },
  {
    label: 'common.la',
    subLabel: 'deg',
    key: 'vLaunchDEG',
    align: 'right',
    formatFn: ({ hit }: ISessionHit) =>
      hit?.invalid
        ? VALUE_FALLBACK
        : (roundToOne(hit?.vLaunchDEG) ?? VALUE_FALLBACK),
    sortRowsFn: (a: ISessionHit, b: ISessionHit, dir: number) =>
      ((a.hit?.vLaunchDEG ?? 0) - (b.hit?.vLaunchDEG ?? 0)) * dir,
  },
  {
    label: 'common.dist',
    subLabel: 'ft',
    key: 'distanceFT',
    align: 'right',
    formatFn: ({ hit }: ISessionHit) =>
      hit?.invalid
        ? VALUE_FALLBACK
        : (roundToZero(hit?.distanceFT) ?? VALUE_FALLBACK),
    sortRowsFn: (a: ISessionHit, b: ISessionHit, dir: number) =>
      ((a.hit?.distanceFT ?? 0) - (b.hit?.distanceFT ?? 0)) * dir,
  },
  {
    label: '',
    key: ACTIONS_KEY,
    formatFn: (hit: ISessionHit) => (
      <Flex gap="2" align="center">
        <IconButton
          variant="soft"
          size="1"
          disabled={!hit.fire.video}
          onClick={() => onPlay(hit.fire)}
        >
          <PlayIcon />
        </IconButton>

        <Switch
          title={
            hit.avg_excluded
              ? 'Excluded from session stats'
              : 'Included in session stats'
          }
          size="1"
          checked={!hit.avg_excluded}
          onCheckedChange={() =>
            onUpdate(hit._id, {
              avg_excluded: !hit.avg_excluded,
            })
          }
        ></Switch>
      </Flex>
    ),
    disableSort: true,
  },
];

export const HitterSession = () => {
  const {
    loading,
    hits,
    pitchTypeFilter,
    outcomeFilter,
    advancedFilters,
    sessions,
    fetchSessions,
    fetchHits,
    updateHit,
  } = useAnalyticsStore();

  const machineCx = useContext(MachineContext);
  const sectionsCx = useContext(SectionsContext);

  const { active } = sectionsCx;
  const { fragments = [] } = active;
  const [sessionID, hitterID] = fragments;

  const [playbackDict, setPlaybackDict] = useState<IExpiringUrlDict>();

  useEffect(() => {
    const paths: string[] = hits
      .map((m) => m.fire.video?.path ?? '')
      .filter((m) => m.length > 0);

    if (paths.length === 0) {
      return;
    }

    console.debug(`signing URLs for ${paths.length} video paths...`);

    MainService.getInstance()
      .signUrls(paths)
      .then((dict) => {
        setPlaybackDict(dict);
      });
  }, [hits]);

  // e.g. linked directly to a hitter report without going through the sessions table first
  useEffect(() => {
    if (sessions.length > 0) {
      return;
    }

    fetchSessions({ machineID: machineCx.machine.machineID });
  }, [sessions, machineCx.machine.machineID]);

  useEffect(() => {
    if (!hitterID) {
      return;
    }

    if (!sessionID) {
      return;
    }

    if (sessions.length === 0) {
      return;
    }

    fetchHits({
      hitterID,
      sessionID,
    });
  }, [sessions, hitterID, sessionID]);

  const [playbackDialog, setPlaybackDialog] = useState<number>();

  const filteredHits = useMemo(
    () =>
      hits.filter((m) => {
        const { pitch, hit } = m;

        if (!pitch?._id) {
          return false;
        }

        if (
          pitchTypeFilter.length > 0 &&
          (!pitch || !pitchTypeFilter.includes(pitch.type))
        ) {
          // Filter by pitch type
          return false;
        }

        // Filter by outcome
        if (
          outcomeFilter.length > 0 &&
          (!hit?.outcome || !outcomeFilter.includes(hit.outcome))
        ) {
          return false;
        }

        if (!advancedFilters) {
          return true;
        }

        const {
          speedMPH: speedMPHFilter,
          xBreakIN: xBreakINFilter,
          zBreakIN: zBreakINFilter,
          exitMPH: exitMPHFilter,
          vLaunchDEG: vLaunchDEGFilter,
          distanceFT: distanceFTFilter,
        } = advancedFilters;

        const pitchFilters = [speedMPHFilter, xBreakINFilter, zBreakINFilter];
        const hitFilters = [exitMPHFilter, vLaunchDEGFilter, distanceFTFilter];

        const hasPitchFilters = pitchFilters.some((f) => f.value);
        const hasHitFilters = hitFilters.some((f) => f.value);

        // Exclude rows with no pitch data if we're filtering by pitch
        if (hasPitchFilters && !pitch) {
          return false;
        }

        // Exclude rows with no hit data if we're filtering by hit
        if (hasHitFilters && !hit) {
          return false;
        }

        const fallsInRange = (
          range: [number, number] | null,
          value?: number
        ) => {
          // no filter applied
          if (!range) {
            return true;
          }

          if (value === undefined) {
            return false;
          }

          return value >= range[0] && value <= range[1];
        };

        // Filter by advanced filter sliders
        if (!fallsInRange(speedMPHFilter.value, pitch?.speedMPH)) {
          return false;
        }

        if (!fallsInRange(xBreakINFilter.value, pitch?.xBreakIN)) {
          return false;
        }

        if (!fallsInRange(zBreakINFilter.value, pitch?.zBreakIN)) {
          return false;
        }

        if (!fallsInRange(exitMPHFilter.value, hit?.exitMPH)) {
          return false;
        }

        if (!fallsInRange(vLaunchDEGFilter.value, hit?.vLaunchDEG)) {
          return false;
        }

        if (!fallsInRange(distanceFTFilter.value, hit?.distanceFT)) {
          return false;
        }

        return true;
      }),
    [hits, pitchTypeFilter, outcomeFilter, advancedFilters]
  );

  const hitsWithVideos = useMemo(
    () => filteredHits.filter((h) => h.fire.video?.path),
    [filteredHits]
  );

  // defines the starting hit for the dialog, when user clicks to play a specific video
  const [hitIndex, setHitIndex] = useState<number>(-1);

  const pagination: ITablePageable = {
    total: filteredHits.length,
    enablePagination: true,
    identifier: IDENTIFIER,
    pageSizes: PAGE_SIZES,
  };

  const sort: ITableSortable = {
    enableSort: true,
  };

  const columns = useMemo(
    () =>
      generateHitterSessionColumns({
        onUpdate: updateHit,
        onPlay: async (fire) => {
          // if we want to enable the button and show toasts to upsell
          if (!fire.video) {
            if (!machineCx.machine.enable_trajekt_vision) {
              NotifyHelper.warning({
                message_md: t('common.enable-trajekt-vision-msg'),
              });
            }

            // machine can support stereo but there isn't a video (yet) associated with this shot
            NotifyHelper.warning({
              message_md: t('common.no-video-playback-msg'),
            });
            return;
          }

          // tells the dialog where to start, is not synced with next/prev triggers
          setHitIndex(hitsWithVideos.findIndex((h) => h.fire._id === fire._id));

          // show the dialog
          setPlaybackDialog(Date.now());
        },
      }),
    [hitsWithVideos, setHitIndex, setPlaybackDialog]
  );

  return (
    <ErrorBoundary componentName="HitterSession">
      <FlexTableWrapper
        gap={RADIX.FLEX.GAP.SECTION}
        // Breadcrumbs and summary rendered in HitterSesionHeader
        header={<HitterSessionHeader />}
        table={
          <CommonTableHoC
            id={IDENTIFIER}
            displayColumns={columns}
            displayData={filteredHits}
            loading={loading}
            toolbarContent={<HitterSessionToolbar />}
            {...pagination}
            {...sort}
            vFlex
          />
        }
      />

      {playbackDialog && hitIndex !== -1 && playbackDict && (
        <HitterSessionVideoDialog
          identifier="HitterSessionShotVideo"
          title="common.shot-data"
          defaultIndex={hitIndex}
          hits={hitsWithVideos}
          urls={playbackDict}
          onClose={() => setPlaybackDialog(undefined)}
        />
      )}
    </ErrorBoundary>
  );
};
